import PlotlyChart from './PlotlyChart'
import { prepareLineChartData, resolveColumnRef } from '../utils/chartDataUtils'
import { COLORS, mergeLayout, mergeConfig } from '../utils/chartTheme'

/**
 * LineChartV2 - A line chart component with support for derived columns and series grouping
 *
 * @param {Object} props
 * @param {Array} props.data - Raw data array from the analytics query
 * @param {Object} props.dimensionInfo - Dimension lookup data (e.g., { clientId: [{ id: 1, name: 'Client A' }] })
 * @param {Object} props.chartOptions - Chart configuration options
 * @param {Array} props.chartOptions.columns - Unified column definitions (shared with Table)
 * @param {string|Object} props.chartOptions.x - X-axis column reference (id, source string, or full definition)
 * @param {string|Object} props.chartOptions.y - Y-axis column reference (or array for multiple y axes)
 * @param {string|Object} props.chartOptions.seriesBy - Column reference to create series by
 * @param {string} props.chartOptions.title - Chart title
 * @param {Object} props.chartOptions.layout - Additional Plotly layout options
 * @param {Object} props.chartOptions.config - Additional Plotly config options
 *
 * Column definition format:
 * {
 *   id: string,                 // Optional identifier for referencing this column
 *   source: string | function,  // Property name, dot notation (e.g., 'clientId.name'), or (row, dimensionInfo) => value
 *   label: string,              // Display label for axis/legend
 *   kind: 'date' | 'number',    // Value type
 *   format: 'percentage' | 'currency' | 'number' | function,  // Value formatting
 *   dateFormat: string,         // date-fns format string for dates
 *   decimalPlaces: number       // Decimal places for number formatting
 * }
 *
 * Example usage with unified columns:
 * <LineChartV2
 *   data={data}
 *   dimensionInfo={dimensionInfo}
 *   chartOptions={{
 *     title: 'Pickup Rate by Client',
 *     columns: [
 *       { id: 'clientName', source: 'clientId.name', label: 'Client Name' },
 *       { id: 'date', source: 'time', kind: 'date', label: 'Date' },
 *       { id: 'pickupRate', source: (row) => row.count > 0 ? row.countAnswered / row.count : 0, format: 'percentage', label: 'Pickup Rate' }
 *     ],
 *     x: 'date',
 *     y: 'pickupRate',
 *     seriesBy: 'clientName'
 *   }}
 * />
 */
export default function LineChartV2({ data, chartOptions = {}, dimensionInfo = {} }) {
  const {
    columns = [],
    x = { source: 'time', kind: 'date', label: 'Time' },
    y = { source: 'count', label: 'Count' },
    seriesBy,
    comparePrevious,
    title,
    layout: customLayout = {},
    config: customConfig = {}
  } = chartOptions

  // Resolve the x column for layout configuration
  const xColumn = resolveColumnRef(x, columns)

  // When comparing periods, split data by comparisonPeriod
  const hasComparison = comparePrevious && data?.some(r => r.comparisonPeriod !== undefined)
  const currentData = hasComparison ? data.filter(r => r.comparisonPeriod === 0) : data
  const previousData = hasComparison ? data.filter(r => r.comparisonPeriod === -1) : []

  // Compute time offset to align previous period with current period on x-axis
  let timeOffsetMs = 0
  if (previousData.length > 0 && currentData.length > 0) {
    const xSource = xColumn.source || xColumn.id || 'time'
    const currentDates = currentData.map(r => new Date(r[xSource])).filter(d => !isNaN(d))
    const previousDates = previousData.map(r => new Date(r[xSource])).filter(d => !isNaN(d))
    if (currentDates.length > 0 && previousDates.length > 0) {
      const currentMin = Math.min(...currentDates)
      const previousMin = Math.min(...previousDates)
      timeOffsetMs = currentMin - previousMin
    }
  }

  // Support multiple y columns
  const yRefs = Array.isArray(y) ? y : [y]

  // Build traces from data subset
  const buildTraces = (subset) => {
    let traces = []
    let yConfig = {}

    if (yRefs.length > 1 && !seriesBy) {
      for (const yRef of yRefs) {
        const yCol = resolveColumnRef(yRef, columns)
        const { traces: t, yAxisConfig: yc } = prepareLineChartData({
          data: subset, dimensionInfo, columns, x, y: yRef, seriesBy: null
        })
        for (const trace of t) {
          trace.name = yCol.label || yCol.source
          traces.push(trace)
        }
        yConfig = yc
      }
    } else {
      const { traces: t, yAxisConfig: yc } = prepareLineChartData({
        data: subset, dimensionInfo, columns, x, y: yRefs[0], seriesBy
      })
      traces = t
      yConfig = yc
    }
    return { traces, yAxisConfig: yConfig }
  }

  const { traces: currentTraces, yAxisConfig } = buildTraces(currentData)
  let allTraces = [...currentTraces]

  // Add previous period traces as dotted lines with shifted x-values
  if (previousData.length > 0) {
    const { traces: prevTraces } = buildTraces(previousData)
    for (let i = 0; i < prevTraces.length; i++) {
      const trace = prevTraces[i]
      // Shift x-values forward to align with current period
      if (timeOffsetMs > 0) {
        trace.x = trace.x.map(xVal => {
          const d = new Date(xVal)
          return isNaN(d) ? xVal : new Date(d.getTime() + timeOffsetMs).toISOString()
        })
      }
      trace.name = `${trace.name} (previous)`
      trace.showlegend = true
      allTraces.push(trace)
    }
  }

  // Apply theme colors — current period solid, previous period dotted with same color
  const currentCount = currentTraces.length
  allTraces.forEach((trace, i) => {
    if (!trace.line?.color && !trace.marker?.color) {
      const isPrevious = i >= currentCount
      const colorIndex = isPrevious ? (i - currentCount) : i
      const color = COLORS[colorIndex % COLORS.length]
      trace.line = {
        ...trace.line,
        color,
        width: isPrevious ? 2 : 2.5,
        dash: isPrevious ? 'dot' : 'solid',
        shape: 'linear',
      }
      trace.marker = { ...trace.marker, color, size: isPrevious ? 4 : 6 }
      if (isPrevious) trace.opacity = 0.6
    }
  })

  // Build layout
  const layout = mergeLayout({
    ...(title ? { title: { text: title, font: { size: 15, color: '#334155' }, x: 0, xanchor: 'left', pad: { l: 4 } } } : {}),
    xaxis: {
      title: { text: xColumn.label, font: { size: 12, color: '#94a3b8' } },
      ...(xColumn.kind === 'date' ? { type: 'date' } : {}),
    },
    yaxis: { ...yAxisConfig },
    margin: { t: title ? 48 : 24, r: 16, b: 44, l: 52 },
    ...customLayout,
  })

  const config = mergeConfig(customConfig)

  return (
    <PlotlyChart
      data={allTraces}
      useResizeHandler={true}
      layout={layout}
      config={config}
      style={{ width: '100%', height: '100%' }}
    />
  )
}
